import { Injectable } from '@angular/core';
import { ConfirmationOrder } from '@models/confirmation-order';
import { IContract } from '@models/contract.class';
import { MARKET_STATUS_TYPE } from '@models/market.class';
import { SearchBoxIssueItem } from '@models/search-box-issue-item';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { AppState, OPERATION_RESET, USER_ROLES } from 'src/app';
import { selectContractsAccountsAccounts } from 'src/app/state/selectors/contract-accounts-selectors';
import { selectContractId } from 'src/app/state/selectors/current-contract.selectors';
import { selectCurrentSymbol } from 'src/app/state/selectors/current-symbol.selectors';
import {
  getMarketName,
  MarketItem,
  SharedSolaceStreamService,
} from 'src/solace';
import { NotificationService } from './notification.service';
import { OperationService } from './operation.service';

@Injectable()
export class PuppetService {
  public canOperate$ = new Observable<boolean>();
  public buyingPower$ = new Observable<number>();
  public selectedIssue$ = new Observable<IssuePuppetData>();
  public position$ = new Observable<IssuePosition>();
  public marketTime$ = new Observable<any>();

  private positionSubject$ = new Subject<IssuePosition>();
  private isInternalUser$ = new BehaviorSubject(false);
  private isDiscretionalContract$ = new BehaviorSubject(false);

  constructor(
    private readonly store: Store<AppState>,
    private readonly sharedSolaceStreamService: SharedSolaceStreamService,
    private readonly operationService: OperationService,
    private readonly notificationService: NotificationService,
  ) {
    this.createObservables();
  }

  public placeOrder$(order: ConfirmationOrder): Observable<any> {
    // TODO: If we reuse this in non-USA orders, we should change
    // the method being called depending on isUsaContract
    return this.getCurrentComContractAndAccount().pipe(
      switchMap(({ contractId, accountId }) =>
        this.operationService
          .registerUsaOrder(contractId, accountId, order)
          .pipe(
            tap(() => {
              this.notificationService.showSuccess({
                message: `Se ha generado una orden de ${
                  order.isBuy ? 'compra' : 'venta'
                } de ${order.issueID}`,
              });

              this.dispatchOperationReset();
            }),
            catchError((error) => {
              this.notificationService.showError({
                message: `No se pudo crear la orden de ${
                  order.isBuy ? 'compra' : 'venta'
                }, intenta más tarde`,
              });
              return Observable.throw(error);
            }),
          ),
      ),
    );
  }

  /* ------------------------ TEMPORARY PUBLIC METHODS ----------------------- */
  // Ideally, this service should handle the subscription to contract changes,
  // but for now, that logic is still in tradeCapitals, and requires us to expose
  // updaters for contract properties (canOperate/isDiscretional), and position.

  public updateCanOperate(userRole: number) {
    if (userRole) {
      this.isInternalUser$.next(this.isInternalUser(userRole));
    }
  }

  public updateIsDiscretional(isDiscretional: boolean) {
    this.isDiscretionalContract$.next(isDiscretional);
  }

  public updatePosition(position: any) {
    this.positionSubject$.next(position);
  }

  public isInternalUser(userRole: number) {
    return userRole === USER_ROLES.RI || userRole === USER_ROLES.SUBDIRECTOR;
  }

  public isReadOnlyContract(userRole: number) {
    return userRole === USER_ROLES.READONLY;
  }

  /* -------------------------------------------------------------------------- */

  private createObservables() {
    this.canOperate$ = this.observeCanOperate();
    this.buyingPower$ = this.observeBuyingPower();
    this.selectedIssue$ = this.observeSelectedIssue();
    this.position$ = this.observePosition();
    this.marketTime$ = this.observeMarketTime();
  }

  private observeMarketTime() {
    return this.store.select('marketOperationTime').pipe(
      map((value: any) => value.toJS()),
      first(),
    );
  }

  private observePosition() {
    return this.positionSubject$.asObservable().pipe(startWith({} as any));
  }

  private observeSelectedIssue() {
    return this.store.select(selectCurrentSymbol).pipe(
      switchMap((symbol: SearchBoxIssueItem) =>
        this.obtainIssueDetailsFromSolace(symbol),
      ),
      distinctUntilChanged(),
      startWith({} as any),
    );
  }

  private observeBuyingPower() {
    return this.store.select('currentContract').pipe(
      map((currentContract: any) => currentContract.toJS() as IContract),
      map(
        (contract) =>
          (contract && contract.totals && contract.totals.buyingPower) || 0,
      ),
    );
  }

  private observeCanOperate() {
    return combineLatest(
      this.isInternalUser$,
      this.isDiscretionalContract$,
    ).pipe(
      map(
        ([isInternalUser, isDiscretional]) =>
          !isInternalUser && !isDiscretional,
      ),
    );
  }

  /* -------------------------------------------------------------------------- */

  private getCurrentComContractAndAccount() {
    return combineLatest(
      this.store.select(selectContractId),
      this.store.select(selectContractsAccountsAccounts),
    ).pipe(
      filter(
        ([contractId, accounts]) =>
          !!contractId && !!accounts && accounts.hasOwnProperty(contractId),
      ),
      map(([contractId, accounts]) => accounts[contractId]),
      first(),
    );
  }

  private dispatchOperationReset() {
    this.store.dispatch({
      type: OPERATION_RESET,
      payload: { reset: true },
    });
  }

  /* --------------------------------- SOLACE --------------------------------- */

  /**
   * This will obtain the details of an issue using Solace
   * @param issue Issue to get the info of. Contains at least instrumentType and issueId
   */
  private obtainIssueDetailsFromSolace(
    issue: SearchBoxIssueItem,
  ): Observable<IssuePuppetData> {
    if (!(issue && issue.issueID && issue.instrumentType !== undefined)) {
      return of({} as IssuePuppetData);
    }

    const marketObservable$ =
      this.sharedSolaceStreamService.getObservableSymbol<MarketItem>(
        getMarketName(issue.instrumentType),
        issue.issueID,
        'alerts',
      );

    return marketObservable$.pipe(
      map((symbol) => {
        if (!symbol || !symbol.hechos || !symbol.hechos.body) {
          return undefined;
        }

        return {
          ...issue,
          openPrice: symbol.hechos.body.openPrice,
          variationPercentage: symbol.hechos.body.variationPercentage,
          lastPrice: symbol.hechos.body.lastPrice,
        };
      }),
    );
  }
}

interface IssuePuppetData {
  instrumentType: number;
  issueID: string;
  issueName: string;
  issue: string;
  weightedAveragePrice: number;
  openPrice: number;
  variationPercentage: number;
  lastPrice: number;
  instrumentId?: string;
  status?: MARKET_STATUS_TYPE;
}

interface IssuePosition {
  closePrice: number;
  costAmount: number;
  ctoProm: number;
  plusMinus: number;
  shares: number;
  valueType: number;
  var: number;
  marketValue?: number;
}
